import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';

import cosmicOrangeFrame from '@/assets/iphone-cosmic-orange-frame.png';
import type { ProspectPreviewContent } from '@/types';

const rendererUrl = (
  import.meta.env.VITE_PREVIEW_RENDERER_URL ??
  'https://everyvisit-preview-renderer.pages.dev'
).replace(/\/$/, '');
const rendererOrigin = new URL(rendererUrl).origin;
const PREVIEW_WIDTH = 438;

export type ProspectPreviewVideoScene =
  | 'profile'
  | 'profile-scroll-bottom'
  | 'profile-scroll-top'
  | 'tap-first-service'
  | 'open-fab'
  | 'close-fab'
  | 'tap-second-service'
  | 'remove-first-service'
  | 'continue-fab'
  | 'schedule'
  | 'scroll-time'
  | 'confirm'
  | 'done';

export type ProspectPreviewSceneOptions = {
  durationMs?: number;
  progress?: number;
  offsetPx?: number;
  pointerScale?: number;
  hideFingerWhenDone?: boolean;
  interruptOnUserInput?: boolean;
};

export type ExactProspectPreviewFrameHandle = {
  getElement: () => HTMLDivElement | null;
  showScene: (scene: ProspectPreviewVideoScene, options?: ProspectPreviewSceneOptions) => Promise<void>;
};

const HINT_SCROLL_MS = 1_733;
const HINT_POINTER_SCALE = 2;

export const ExactProspectPreviewFrame = forwardRef<ExactProspectPreviewFrameHandle, {
  content: ProspectPreviewContent;
  interactiveHint?: boolean;
}>(function ExactProspectPreviewFrame({ content, interactiveHint = false }, ref) {
  const fitRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const rendererReadyRef = useRef(false);
  const commandIdRef = useRef(0);
  const pendingCommandsRef = useRef(new Map<number, () => void>());
  const latestContentRef = useRef(content);
  const hintCancelledRef = useRef(false);
  const hintStartedRef = useRef(false);
  const hintTimersRef = useRef<number[]>([]);
  const [fitScale, setFitScale] = useState(1);
  latestContentRef.current = content;

  useLayoutEffect(() => {
    const fit = fitRef.current;
    if (!fit) return;
    const updateScale = () => {
      setFitScale(Math.min(1, fit.getBoundingClientRect().width / PREVIEW_WIDTH));
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(fit);
    return () => observer.disconnect();
  }, []);

  function sendContent() {
    iframeRef.current?.contentWindow?.postMessage(
      {
        source: 'everyvisit-prospect-preview',
        content: latestContentRef.current,
      },
      rendererOrigin,
    );
  }

  function showScene(scene: ProspectPreviewVideoScene, options?: ProspectPreviewSceneOptions) {
    return new Promise<void>((resolve) => {
      const commandId = ++commandIdRef.current;
      pendingCommandsRef.current.set(commandId, resolve);
      iframeRef.current?.contentWindow?.postMessage(
        {
          source: 'everyvisit-prospect-preview-command',
          commandId,
          scene,
          ...options,
        },
        rendererOrigin,
      );
    });
  }

  useImperativeHandle(ref, () => ({
    getElement: () => phoneRef.current,
    showScene,
  }));

  function clearHintTimers() {
    for (const timer of hintTimersRef.current) window.clearTimeout(timer);
    hintTimersRef.current = [];
  }

  function dismissHint() {
    hintCancelledRef.current = true;
    clearHintTimers();
  }

  function wait(ms: number) {
    return new Promise<void>((resolve) => {
      hintTimersRef.current.push(window.setTimeout(resolve, ms));
    });
  }

  async function playInteractiveHint() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;
    await wait(720);
    if (hintCancelledRef.current) return;
    await showScene('profile-scroll-bottom', {
      durationMs: HINT_SCROLL_MS,
      pointerScale: HINT_POINTER_SCALE,
      hideFingerWhenDone: false,
      interruptOnUserInput: true,
    });
    if (hintCancelledRef.current) return;
    await wait(280);
    if (hintCancelledRef.current) return;
    await showScene('profile-scroll-top', {
      durationMs: HINT_SCROLL_MS,
      pointerScale: HINT_POINTER_SCALE,
      hideFingerWhenDone: false,
      interruptOnUserInput: true,
    });
    if (hintCancelledRef.current) return;
    await wait(280);
    if (hintCancelledRef.current) return;
    if (content.services.length >= 1) {
      await showScene('tap-first-service', {
        pointerScale: HINT_POINTER_SCALE,
        hideFingerWhenDone: true,
        interruptOnUserInput: true,
      });
      if (hintCancelledRef.current) return;
    }
  }

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== rendererOrigin) return;
      if (event.data?.source === 'everyvisit-prospect-preview-ready') {
        rendererReadyRef.current = true;
        sendContent();
      }
      if (event.data?.source === 'everyvisit-prospect-preview-content-ready') {
        if (!interactiveHint || hintStartedRef.current) return;
        hintStartedRef.current = true;
        hintCancelledRef.current = false;
        void playInteractiveHint();
      }
      if (event.data?.source === 'everyvisit-prospect-preview-user-interrupt') {
        dismissHint();
      }
      if (event.data?.source === 'everyvisit-prospect-preview-command-complete') {
        const commandId = Number(event.data.commandId);
        pendingCommandsRef.current.get(commandId)?.();
        pendingCommandsRef.current.delete(commandId);
      }
      if (event.data?.source === 'everyvisit-prospect-preview-api-blocked') {
        console.error('The preview renderer blocked an operational API call.', event.data.url);
      }
    };

    window.addEventListener('message', handleMessage);
    if (rendererReadyRef.current) sendContent();
    return () => {
      dismissHint();
      hintStartedRef.current = false;
      window.removeEventListener('message', handleMessage);
    };
  }, [content, interactiveHint]);

  useEffect(() => {
    if (!interactiveHint) return;
    const onBlur = () => {
      if (document.activeElement === iframeRef.current) dismissHint();
    };
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, [interactiveHint]);

  return (
    <div ref={fitRef} className="prospect-exact-fit">
      <div
        ref={phoneRef}
        className="prospect-phone prospect-phone-exact"
        style={{ transform: `scale(${fitScale})` }}
      >
        <div className="prospect-preview-layer prospect-preview-layer-bottom">
          <iframe
            ref={iframeRef}
            className="prospect-exact-frame"
            src={rendererUrl}
            title={`Interactive Everyvisit preview for ${content.businessName}`}
            sandbox="allow-scripts allow-same-origin"
            onLoad={() => {
              rendererReadyRef.current = true;
              sendContent();
            }}
          />
        </div>
        <img
          className="prospect-preview-layer prospect-preview-layer-top"
          src={cosmicOrangeFrame}
          alt=""
          draggable={false}
          aria-hidden="true"
        />
      </div>
    </div>
  );
});
